import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { getDB } from '../utils/crypto';
import { v4 as uuidv4 } from 'uuid';

interface UserRegistrationProps {
  onComplete: (redirectPath?: string) => void;
}

const UserRegistration: React.FC<UserRegistrationProps> = ({ onComplete }) => {
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Check if user came from an invite link
  const hasInviteCode = location.pathname === '/invite' || location.hash.length > 1;

  // Listen for online/offline events
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Bitte gib einen Benutzernamen ein');
      return;
    }
    
    if (!acceptedPrivacy) {
      setError('Bitte akzeptiere die Datenschutzerklärung, um fortzufahren');
      return;
    }
    
    // Check if offline before attempting registration
    if (!navigator.onLine) {
      setError('Keine Internetverbindung. Registrierung erfordert eine Online-Verbindung.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First, check if this username is stored locally in IndexedDB
      const db = await getDB();
      const storedCredentials = await db.get('user_data', 'credentials');
      const storedUserInfo = await db.get('user_data', 'user_info');
      
      // If username matches stored data, try to login instead of registering
      if (storedUserInfo && storedUserInfo.username.toLowerCase() === username.trim().toLowerCase()) {
        console.log('Username matches stored user, attempting auto-login...');
        
        // Try to sign in with stored credentials
        if (storedCredentials) {
          try {
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email: storedCredentials.email,
              password: storedCredentials.password
            });
            
            if (!signInError) {
              console.log('Auto-login successful!');
              // If user came from invite link, redirect to invite handler
              const redirectPath = hasInviteCode ? `/invite${location.hash}` : undefined;
              onComplete(redirectPath);
              return;
            } else {
              console.error('Auto-login failed:', signInError);
              // Continue with registration if auto-login fails
            }
          } catch (loginError) {
            console.error('Error during auto-login:', loginError);
            // Continue with registration if auto-login fails
          }
        }
        
        // If we get here, auto-login didn't work, so inform user
        setError('Dieser Benutzername gehört dir bereits. Wenn du ein Passwort gesetzt hast, gib es bitte ein.');
        setIsLoading(false);
        return;
      }
      
      // Check if username already exists using database function
      const { data: isAvailable, error: checkError } = await (supabase.rpc as any)(
        'is_username_available', 
        { check_username: username.trim() }
      );
      
      if (checkError) {
        console.error('Error checking username:', checkError);
        // Continue anyway - the database trigger will handle uniqueness
      } else if (isAvailable === false) {
        // Username exists but wasn't in local storage - try to login with provided credentials
        if (password.trim() && email.trim()) {
          console.log('Username exists, trying to login with provided email/password...');
          try {
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email: email.trim(),
              password: password.trim()
            });
            
            if (!signInError) {
              console.log('Login successful with provided credentials!');
              
              // Store credentials in IndexedDB
              const db = await getDB();
              await db.put('user_data', {
                id: 'credentials',
                email: email.trim(),
                password: password.trim(),
                username: username.trim(),
                timestamp: new Date().toISOString()
              });
              
              // Get user info
              const { data: { user } } = await supabase.auth.getUser();
              await db.put('user_data', {
                id: 'user_info',
                username: username.trim(),
                user_id: user?.id || '',
                email: email.trim(),
                timestamp: new Date().toISOString()
              });
              
              // If user came from invite link, redirect to invite handler
              const redirectPath = hasInviteCode ? `/invite${location.hash}` : undefined;
              onComplete(redirectPath);
              return;
            } else {
              setError('Login fehlgeschlagen. Falsches E-Mail oder Passwort.');
              setIsLoading(false);
              return;
            }
          } catch (loginError) {
            console.error('Error during login attempt:', loginError);
            setError('Login fehlgeschlagen. Bitte überprüfe deine Zugangsdaten.');
            setIsLoading(false);
            return;
          }
        }
        
        setError('Dieser Benutzername ist bereits vergeben. Bitte wähle einen anderen Namen oder gib E-Mail und Passwort ein, um dich anzumelden.');
        setIsLoading(false);
        return;
      }
      
      // Generate email if not provided
      const userEmail = email.trim() || `${username.replace(/\s+/g, '_').toLowerCase()}_${uuidv4().substring(0, 8)}@raetseljagd.local`;
      
      // Generate password if not provided
      const userPassword = password.trim() || uuidv4();
      
      // Register with Supabase
      const { data, error: authError } = await supabase.auth.signUp({
        email: userEmail,
        password: userPassword,
        options: {
          data: {
            username,
          }
        }
      });
      
      if (authError) {
        throw new Error(authError.message);
      }
      
      // Make sure IndexedDB is properly initialized
      try {
        const db = await getDB();
        
        // Store credentials in IndexedDB if password was generated (not user-provided)
        if (!password.trim()) {
          await db.put('user_data', {
            id: 'credentials',
            email: userEmail,
            password: userPassword,
            username: username,
            timestamp: new Date().toISOString()
          });
        }
        
        // Also store username and user ID regardless of password source
        await db.put('user_data', {
          id: 'user_info',
          username: username,
          user_id: data.user?.id || '',
          email: userEmail,
          timestamp: new Date().toISOString()
        });
        
        console.log('User data successfully stored in IndexedDB');
      } catch (dbError) {
        console.error('Error storing user data in IndexedDB:', dbError);
        // Continue anyway, as the user is registered in Supabase
      }
      
      // Complete registration
      // If user came from invite link, redirect to invite handler
      const redirectPath = hasInviteCode ? `/invite${location.hash}` : undefined;
      onComplete(redirectPath);
      
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      
      // Check for duplicate username error
      if (errorMessage.includes('duplicate key') || 
          errorMessage.includes('unique constraint') ||
          errorMessage.includes('username') && errorMessage.includes('already exists')) {
        setError('Dieser Benutzername ist bereits vergeben. Bitte wähle einen anderen Namen.');
      } else {
        setError(`Registrierung fehlgeschlagen: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="user-registration">
      <h2>Willkommen zur Rätseljagd!</h2>
      
      {!isOnline && (
        <div className="offline-warning">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="1" y1="1" x2="23" y2="23"></line>
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
            <line x1="12" y1="20" x2="12.01" y2="20"></line>
          </svg>
          <span>Keine Internetverbindung - Registrierung erfordert eine Online-Verbindung</span>
        </div>
      )}
      
      <p className="registration-intro">
        Bitte erstelle ein Profil, um an der Rätseljagd teilzunehmen.
        Wenn du bereits einen Benutzernamen hast, gib ihn einfach erneut ein, um dich anzumelden.
      </p>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="username">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Dein Name"
            required
            autoFocus
          />
          <p className="field-hint">
            Da dies eine reale Veranstaltung ist, wäre dein echter Name bevorzugt, aber nicht verpflichtend.
            Der Name muss eindeutig sein.
          </p>
        </div>
        
        <div className="form-group">
          <label htmlFor="email">
            E-Mail <span className="optional">(optional)</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.de (optional)"
          />
          <p className="field-hint">
            Wenn keine E-Mail angegeben wird, wird eine lokale E-Mail generiert.
          </p>
        </div>
        
        <div className="form-group">
          <label htmlFor="password">
            Passwort <span className="optional">(optional)</span>
          </label>
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort (optional)"
            />
            <button 
              type="button" 
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "Verbergen" : "Zeigen"}
            </button>
          </div>
          <p className="field-hint">
            Wenn kein Passwort angegeben wird, wird ein sicheres Passwort generiert und lokal gespeichert.
            <br />
            <strong>Hinweis:</strong> Selbst gewählte Passwörter werden NICHT lokal gespeichert.
          </p>
        </div>
        
        <div className="form-group privacy-checkbox">
          <label htmlFor="privacy-checkbox" className="checkbox-label">
            <input
              type="checkbox"
              id="privacy-checkbox"
              checked={acceptedPrivacy}
              onChange={(e) => setAcceptedPrivacy(e.target.checked)}
              required
            />
            <span>
              Ich habe die <Link to="/datenschutz" target="_blank">Datenschutzerklärung</Link> gelesen 
              und stimme der Nutzung von Cloudflare (CDN), Supabase (Datenbank) und GitHub (Pages) zu.
              <span className="required"> *</span>
            </span>
          </label>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <button 
          type="submit" 
          className="register-button"
          disabled={isLoading}
        >
          {isLoading ? "Registriere..." : "Registrieren und Starten"}
        </button>
      </form>
      
      <p className="registration-note">
        Die Registrierung ist nur auf diesem Gerät gültig. Du kannst später einer Gruppe beitreten,
        um mit anderen Teilnehmern zusammenzuarbeiten.
      </p>
    </div>
  );
};

export default UserRegistration;